import { supabase } from "@/integrations/supabase/client";
import { stripePromise } from "@/integrations/stripe/client";
import { EVENTS, track } from "@/lib/analytics";

export interface ChargeSavedCardLineItem {
  name: string;
  amount: number;
}

export type ChargeSavedCardResult =
  | { kind: "paid" }
  | { kind: "requires_checkout" }
  | { kind: "failed"; message: string };

const supabaseFunctionsUrl = `${
  import.meta.env.VITE_SUPABASE_URL || "https://nbmpyagsjfheforuzwvf.supabase.co"
}/functions/v1`;

/**
 * Charge the company's saved card for an existing pending order.
 * Mirrors OrderCreate's post-insert charge path.
 */
export async function chargeSavedCardForOrder(args: {
  companyId: string;
  orderId: string;
  amountAud: number;
  description: string;
  lineItems: ChargeSavedCardLineItem[];
}): Promise<ChargeSavedCardResult> {
  const { data: company } = await supabase
    .from("companies")
    .select("stripe_customer_id, saved_card_last4")
    .eq("id", args.companyId)
    .single();

  if (!company?.stripe_customer_id || !company?.saved_card_last4) {
    return { kind: "requires_checkout" };
  }

  const { data: { session } } = await supabase.auth.getSession();
  const chargeRes = await fetch(`${supabaseFunctionsUrl}/charge-saved-card`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : {}),
    },
    body: JSON.stringify({
      companyId: args.companyId,
      orderId: args.orderId,
      amount: args.amountAud,
      description: args.description,
      lineItems: args.lineItems,
    }),
  });

  if (!chargeRes.ok) {
    const err = await chargeRes.json().catch(() => ({}));
    return {
      kind: "failed",
      message:
        (err as { error?: string }).error ||
        "We couldn't charge your saved card. Try again or use secure checkout.",
    };
  }

  const result = await chargeRes.json();
  if (result.success) {
    return { kind: "paid" };
  }

  if (result.requires_action && result.client_secret) {
    try {
      const stripe = await stripePromise;
      if (stripe) {
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          result.client_secret,
        );
        if (confirmError) {
          track(EVENTS.ORDER_PAYMENT_FAILED, {
            order_id: args.orderId,
            stage: "3ds_challenge",
            reason: confirmError.code ?? confirmError.type ?? null,
          });
          return {
            kind: "failed",
            message:
              confirmError.message ||
              "That didn't go through at the verification step. Try again?",
          };
        }
        if (paymentIntent?.status === "succeeded") {
          return { kind: "paid" };
        }
      }
    } catch {
      return { kind: "requires_checkout" };
    }
  }

  return { kind: "requires_checkout" };
}
